import { useRef } from "react";

import { formatNumber } from "./licensingTheme";
import { useBarsRise } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const DAYS = [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
const REMINDERS = { 3: "Reminder email", 1: "Last reminder email" };

/**
 * Every live trial, by how long it has left.
 *
 * Read right to left it is a queue: the right-hand end is who decides this
 * week. The two marked days are when the product emails them, so a tall bar
 * under a marker is a reminder about to go out.
 */
export default function TrialCountdown({ countdown, loading, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const byDay = new Map((countdown ?? []).map((row) => [Number(row.daysLeft), Number(row.count) || 0]));
  const max = Math.max(1, ...DAYS.map((d) => byDay.get(d) ?? 0));
  const total = DAYS.reduce((sum, d) => sum + (byDay.get(d) ?? 0), 0);
  const thisWeek = DAYS.filter((d) => d <= 7).reduce((sum, d) => sum + (byDay.get(d) ?? 0), 0);

  useBarsRise(scope, [JSON.stringify(countdown ?? [])], { stagger: 0.03 });

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-countdown-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-countdown-title" className="mcap-lic-panel-title">
            Trials ending
          </h3>
          <p className="mcap-lic-panel-sub">
            {loading
              ? " "
              : total
                ? `${formatNumber(total)} on free trial · ${formatNumber(thisWeek)} end this week`
                : "Nobody is on a free trial right now"}
          </p>
        </div>
      </header>

      <div className="mcap-lic-bars is-countdown">
        {DAYS.map((day) => {
          const count = byDay.get(day) ?? 0;
          const reminder = REMINDERS[day];
          return (
            <button
              key={day}
              type="button"
              className={`mcap-lic-bar-col ${reminder ? "is-reminder" : ""} ${count ? "" : "is-empty"}`}
              {...bind({
                title: `${day} day${day === 1 ? "" : "s"} left`,
                subtitle: reminder ? `${reminder} is sent on this day` : undefined,
                rows: [{ label: "Teachers", value: formatNumber(count), color: reminder ? "#c9972b" : "#1f6f8b" }],
                footer: count ? "Click to see who" : undefined,
              })}
              onClick={() => {
                if (!count) return;
                hide();
                onDrill({
                  type: "cohort",
                  title: `Trials with ${day} day${day === 1 ? "" : "s"} left`,
                  filters: { trialDays: String(day) },
                });
              }}
              aria-label={`${count} trial${count === 1 ? "" : "s"} with ${day} day${day === 1 ? "" : "s"} left${
                reminder ? `, ${reminder.toLowerCase()} day` : ""
              }`}
            >
              <span className="mcap-lic-bar-value">{count || ""}</span>
              <span className="mcap-lic-bar-track">
                {count > 0 && <span className="mcap-lic-bar-fill" data-bar style={{ height: `${Math.max(8, (count / max) * 100)}%` }} />}
              </span>
              <span className="mcap-lic-bar-label">{day}</span>
            </button>
          );
        })}
      </div>
      <div className="mcap-lic-axis-note">
        <span>Days left in trial</span>
        <span className="mcap-lic-legend-item">
          <span className="mcap-lic-legend-dot is-reminder" aria-hidden="true" /> reminder email sent
        </span>
      </div>

      <ChartTooltip tip={tip} />
    </section>
  );
}
